import * as React from 'react';
import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { popupData, PopupNames } from 'core';
import { usePopupStateOperations } from 'core/hooks';
import { Button, Heading } from 'atoms';
import { Colors } from 'services/ColorService';
import { getFontFamily } from 'services/FontService';

export interface IZoomDangerConfirm {
  title: string;
  body: string;
  /** Exact string the operator must type to enable the confirm button. */
  confirmString: string;
  onConfirm: () => void | Promise<void>;
}

/**
 * Destructive-action modal: the confirm button stays disabled until the
 * operator types the exact `confirmString` (e.g. `DELETE 12`).
 */
export const DangerConfirmPopup = memo(() => {
  const { t } = useTranslation();
  const {
    operations: { hidePopup },
  } = usePopupStateOperations();

  const data = popupData()[PopupNames.zoomDangerConfirm];

  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const close = useCallback(() => {
    hidePopup(PopupNames.zoomDangerConfirm);
  }, [hidePopup]);

  const onConfirm = useCallback(async () => {
    if (!data) {
      return;
    }

    setSubmitting(true);
    try {
      await data.onConfirm();
      close();
    } finally {
      setSubmitting(false);
    }
  }, [data, close]);

  if (!data) {
    return null;
  }

  const confirmEnabled = value === data.confirmString && !submitting;

  return (
    <Wrapper>
      <Heading>{data.title}</Heading>
      <Body>{data.body}</Body>
      <Hint>
        {t('pages.zoom.danger.confirmHint', {
          confirmString: data.confirmString,
        })}
      </Hint>
      <ConfirmInput
        value={value}
        autoFocus
        placeholder={data.confirmString}
        onChange={(event) => setValue(event.target.value)}
      />
      <Buttons>
        <Button
          onClick={close}
          variant="outline"
          colors={{ backgroundColor: Colors.White, color: Colors.Blue }}
        >
          {t('pages.zoom.danger.cancelButton')}
        </Button>
        <Button
          onClick={onConfirm}
          variant="primary"
          disabled={!confirmEnabled}
          loading={submitting}
          colors={{ backgroundColor: Colors.ErrorDefault, color: Colors.White }}
        >
          {t('pages.zoom.danger.confirmButton')}
        </Button>
      </Buttons>
    </Wrapper>
  );
});

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 32px;
  width: 480px;
  box-sizing: border-box;
`;

const Body = styled.p`
  font-family: ${getFontFamily('regular')};
  font-size: 14px;
  line-height: 22px;
  color: ${Colors.Black};
  margin: 16px 0 8px;
`;

const Hint = styled.span`
  font-family: ${getFontFamily('regular')};
  font-size: 13px;
  color: ${Colors.Gray};
  margin-bottom: 8px;
`;

const ConfirmInput = styled.input`
  height: 40px;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 6px;
  padding: 0 12px;
  font-size: 14px;
  font-family: ${getFontFamily('regular')};
  outline: none;

  &:focus {
    border-color: ${Colors.Blue};
  }
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;
`;
